import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useMemo, useState } from 'react';
import {
  ticketTypeSetsGet,
  ticketTypeSetEdit,
  ticketTypeSetDelete,
  ticketTypesGet,
} from 'api/api';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import AddIcon from '@mui/icons-material/Add';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';

import * as Yup from 'yup';
import { FastField, Field, Form, Formik } from 'formik';
import FormGroup from '@mui/material/FormGroup';
import { CheckboxWithLabel, TextField } from 'formik-mui';
import React from 'react';
import moment from 'moment';

export const TicketTypeSets = ({
  selectedOrganization,
}: {
  selectedOrganization: any;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [selected, setSelected] = useState<any | null>(null);
  const queryClient = useQueryClient();

  const alert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  /*
  const { data: getUserAdminOfOrganizationsResponse } = useQuery(
    'getUserAdminOfOrganizations',
    getUserAdminOfOrganizations,
    {
      enabled: true,
      refetchOnWindowFocus: false,
      suspense: true,
    },
  );

  const userAdminOfOrganizations = getUserAdminOfOrganizationsResponse!.data;

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number>(
    userAdminOfOrganizations[0]?.id,
  );
  */

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['ticketTypeSetsGet', selectedOrganization?.id],
    ticketTypeSetsGet(selectedOrganization?.id as number),
    {
      enabled: !!selectedOrganization,
      refetchOnWindowFocus: false,
    },
  );
  const data: any = response?.data;

  const {
    data: ticketTypeResponse,
    isLoading: ticketTypesIsLoading,
    error: ticketTypesGetError,
    refetch: refetchTicketTypes,
  } = useQuery(
    ['ticketTypesGet', selectedOrganization?.id],
    ticketTypesGet(selectedOrganization?.id as number),
    {
      enabled: !!selectedOrganization,
      refetchOnWindowFocus: false,
    },
  );
  const orgTicketTypes: any = ticketTypeResponse?.data;
  //console.log('orgTicketTypes', orgTicketTypes);

  const {
    mutateAsync,
    error: mutateError,
    data: mutateData,
    isLoading: mutateIsLoading,
  } = useMutation('ticketTypeEdit', ticketTypeSetEdit, {
    onError: (err: any) => {
      alert('Error', err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries('eventOptionsGet');
    },
  });

  const { columnDefs, rowData } = useMemo(() => {
    if (!data?.length) {
      return { rowData: [] };
    }
    const columnDefs: ColDef[] = [
      {
        field: 'name',
        headerName: 'Name',
        sortable: true,
        // eslint-disable-next-line sonarjs/no-duplicate-string
        flex: 2,
        minWidth: 210,
        filter: true,
      },
      {
        field: 'createdAt',
        headerName: 'Created at',
        sortable: true,
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ data }) => {
          return moment(data.createdAt).format('YYYY-MM-DD HH:mm');
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Updated at',
        sortable: true,
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ data }) => {
          return moment(data.updatedAt).format('YYYY-MM-DD HH:mm');
        },
      },
    ];

    return { columnDefs, rowData: data };
  }, [data]);

  /*
  const veeziOrg =
    selectedOrganization?.type === 'cinema' &&
    (selectedOrganization?.dataSource === 'VEEZI_EU' ||
      selectedOrganization?.dataSource === 'VEEZI_US')
      ? ''
      : 'This name is imported from Veezi and cannot be changed';
*/

  if (!orgTicketTypes || !data) {
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <React.Fragment key="right">
        <Drawer
          anchor="right"
          open={rightDrawerOpen}
          onClose={() => {
            refetch();
            setRightDrawerOpen(false);
          }}
        >
          <Box sx={{ width: { xs: '100vw', sm: '600px' } }}>
            <Typography
              variant="h1"
              sx={{ fontSize: '1.3em', fontWeight: 500, m: '16px 24px' }}
            >
              {!!selected ? 'Edit ticket type set' : 'Add ticket type set'}
            </Typography>
            <Divider />
            <Box sx={{ padding: '24px' }}>
              <Box sx={{ mb: '20px' }}>
                <Typography sx={{ fontWeight: 500, display: 'inline' }}>
                  Organization:{' '}
                </Typography>
                {selectedOrganization?.name}
              </Box>
              {/*
              {!!selected && (
                <Box sx={{ mb: '16px' }}>
                  <Typography sx={{ fontWeight: 500, display: 'inline' }}>
                    User:{' '}
                  </Typography>
                  {selected?.name}
                </Box>
              )}
              */}

              {orgTicketTypes.length === 0 && (
                <Box sx={{ color: 'red', mb: '16px' }}>
                  This organization does not have any ticket types yet. After
                  you add a ticket type you can select it here.
                </Box>
              )}

              <Formik
                validateOnMount
                initialValues={{
                  id: selected?.id || undefined,
                  name: selected?.name || '',
                  ticketTypes: orgTicketTypes?.map((orgTicketType) =>
                    selected?.ticketTypes?.some(
                      (e) => e.id === orgTicketType.id,
                    )
                      ? {
                          ...orgTicketType,
                          checked: true,
                        }
                      : {
                          ...orgTicketType,
                          checked: false,
                        },
                  ),
                }}
                validationSchema={Yup.object().shape({
                  name: Yup.string().required('Required'),
                })}
                onSubmit={async (values: any, { setSubmitting }) => {
                  console.log('values', values.ticketTypes);

                  values.organizationId = selectedOrganization.id;

                  const mutateData = { ...values };
                  mutateData.ticketTypes = mutateData.ticketTypes.filter(
                    (e) => e.checked,
                  );
                  console.log('mutateData', mutateData.ticketTypes);
                  mutateData.ticketTypes = {
                    connect: mutateData.ticketTypes.map((ticketType) => ({
                      id: ticketType.id,
                    })),
                  };

                  console.log('mutateData2', mutateData);

                  try {
                    await mutateAsync({ values: mutateData });
                  } catch (error: any) {
                    alert(error.error, error.message);
                  }
                  refetch();
                  setSubmitting(false);
                  setRightDrawerOpen(false);
                }}
              >
                {({
                  submitForm,
                  isSubmitting,
                  values,
                  isValid,
                  handleChange,
                  errors,
                  touched,
                  handleBlur,
                }) => (
                  <Form style={{ width: '100%' }}>
                    <Box
                      sx={{
                        width: '100%',
                        marginInline: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: 'relative',
                      }}
                    >
                      <FastField
                        component={TextField}
                        label="Name"
                        name="name"
                        size="small"
                      />
                      <FormGroup>
                        {values.ticketTypes?.map((e, index) => {
                          return (
                            <>
                              <Field
                                key={e.id}
                                component={CheckboxWithLabel}
                                type="checkbox"
                                name={`ticketTypes[${index}].checked`}
                                onChange={handleChange}
                                Label={{ label: e.name }}
                              />
                              <Typography
                                sx={{
                                  opacity: '0.6',
                                  mt: '-5px',
                                  mb: '6px',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {e.description}
                              </Typography>
                            </>
                          );
                        })}
                      </FormGroup>
                      <Box
                        sx={{
                          textAlign: 'right',
                          margin: '16px 0',
                          display: 'flex',
                          flexDirection: 'row',
                        }}
                      >
                        <Button
                          color="primary"
                          disabled={isSubmitting}
                          onClick={() => setRightDrawerOpen(false)}
                          sx={{ padding: '6px 8px' }}
                        >
                          Cancel
                        </Button>

                        <Box sx={{ flex: '1 1 auto' }} />
                        {!!selected && (
                          <Button
                            color="primary"
                            disabled={isSubmitting}
                            onClick={() => setDialogOpen(true)}
                            sx={{ padding: '6px 8px' }}
                          >
                            Delete
                          </Button>
                        )}
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Button
                          color="primary"
                          variant="contained"
                          disabled={isSubmitting || !isValid}
                          sx={{ padding: '6px 24px' }}
                          onClick={submitForm}
                        >
                          {!!selected ? 'Save' : 'Add'}
                        </Button>
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Box>
        </Drawer>
      </React.Fragment>
      <Dialog
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{`${alertTitle}`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`${alertMessage}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Deletion confirmation'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete this? The ticket type set will not be deleted if a future ending event has it.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                const response = await ticketTypeSetDelete(selected.id)();
                setDialogOpen(false);
                if (response) {
                  refetch();
                  setDialogOpen(false);
                  setRightDrawerOpen(false);
                }
              } catch (error: any) {
                refetch();
                setDialogOpen(false);
                //setRightDrawerOpen(false);
                alert(error.error, error.message);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: '1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 0,
            alignItems: 'center',
            gap: 1,
            height: '56px',
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontSize: '1.25rem',
            }}
          >
            Ticket type sets
          </Typography>
          <Button
            color="primary"
            onClick={() => {
              setSelected(null);
              setRightDrawerOpen(true);
            }}
          >
            <AddIcon sx={{ ml: -0.5, mr: 0.5 }} />
            Add new
          </Button>
        </Box>
        <Box
          className="ag-theme-material"
          sx={{
            background: '#000',
            borderRadius: 0,
            margin: '0 -16px -16px',
            display: 'flex',
            overflow: 'hidden',
            flexGrow: 1,
            height: '100%',
            boxShadow:
              'rgb(0 0 0 / 20%) 0px 2px 1px -1px, rgb(0 0 0 / 14%) 0px 1px 1px 0px, rgb(0 0 0 / 12%) 0px 1px 3px 0px',
          }}
        >
          <AgGridReact
            className="ag-pointer-row"
            rowData={rowData}
            pagination={true}
            floatingFiltersHeight={50}
            paginationAutoPageSize={true}
            columnDefs={columnDefs}
            onRowClicked={({ data }) => {
              setSelected(data);
              setRightDrawerOpen(true);
            }}
          ></AgGridReact>
        </Box>
      </Box>
    </>
  );
};
